/**
 * Reserve form "Laat voor mij klaarzetten" inside the reserve dialog (snippets/bbd-reserve-form.liquid).
 * bbd-store-stock-status fills the hidden fields and opens the dialog, this element handles the result
 * and closing.
 *
 * The form submits normally, without fetch: with spam protection on, Shopify's hCaptcha script binds to
 * contact forms and calls form.submit() itself after getting a token, so a fetch would send twice.
 * Shopify redirects back with the dialog closed. This element reopens it with the state Liquid rendered.
 */
class BbdReserveForm extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.#dialog?.addEventListener('click', this.#handleBackdropClick);
    this.#dialog?.addEventListener('cancel', this.#handleCancel);
    this.#restoreState();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.#dialog?.removeEventListener('click', this.#handleBackdropClick);
    this.#dialog?.removeEventListener('cancel', this.#handleCancel);
  }

  /** @returns {HTMLDialogElement|null} */
  get #dialog() {
    return this.closest('dialog');
  }

  /** @returns {HTMLElement|null} */
  get #formPanel() {
    return this.querySelector('[data-bbd-reserve-panel="form"]');
  }

  /** @returns {HTMLElement|null} */
  get #successPanel() {
    return this.querySelector('[data-bbd-reserve-panel="success"]');
  }

  /** @returns {HTMLElement|null} */
  get #errors() {
    return this.querySelector('[data-bbd-reserve-errors]');
  }

  /**
   * After a submit the page reloads with the dialog closed. Reopen it with the result.
   */
  #restoreState() {
    const state = this.#formPanel?.dataset.state;
    const dialog = this.#dialog;
    if (!state || !dialog) return;

    if (!dialog.open) dialog.showModal();
    // The close button describes itself with the confirmation, so screen readers still hear it
    const focusTarget = state === 'success' ? this.querySelector('[data-bbd-reserve-success-focus]') : this.#errors;
    if (focusTarget instanceof HTMLElement) focusTarget.focus();

    // Shopify adds contact_posted and the form anchor. Without them a page refresh does not reopen the dialog
    const url = new URL(window.location.href);
    url.searchParams.delete('contact_posted');
    url.hash = '';
    history.replaceState(history.state, '', url);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (event.target instanceof Element && event.target.closest('[data-bbd-reserve-close]')) {
      this.#close();
    }
  };

  /**
   * The dialog has no padding, so a click on the dialog itself is a click on the backdrop.
   * @param {MouseEvent} event
   */
  #handleBackdropClick = (event) => {
    if (event.target === this.#dialog) this.#close();
  };

  /** @param {Event} event */
  #handleCancel = (event) => {
    event.preventDefault();
    this.#close();
  };

  /**
   * Closes with Horizon's .dialog-modal closing animation. The browser returns focus to the reserve button.
   */
  async #close() {
    const dialog = this.#dialog;
    if (!dialog?.open) return;

    dialog.classList.add('dialog-closing');
    await Promise.allSettled(dialog.getAnimations().map((animation) => animation.finished));
    dialog.classList.remove('dialog-closing');
    dialog.close();

    this.#reset();
  }

  /**
   * Back to the form without old messages, so a next request starts clean.
   * The store block refills the hidden fields on open.
   */
  #reset() {
    const errors = this.#errors;
    if (errors) {
      errors.replaceChildren();
      errors.hidden = true;
    }

    const email = this.querySelector('input[name="contact[email]"]');
    email?.removeAttribute('aria-invalid');
    email?.removeAttribute('aria-describedby');

    if (this.#successPanel && !this.#successPanel.hidden) {
      this.querySelector('form')?.reset();
      this.#successPanel.hidden = true;
      if (this.#formPanel) this.#formPanel.hidden = false;
    }
  }
}

if (!customElements.get('bbd-reserve-form')) {
  customElements.define('bbd-reserve-form', BbdReserveForm);
}
